import { View, TouchableOpacity, StyleSheet, Image } from "react-native";
import { Card, Text } from "react-native-paper";
import { forwardRef, useCallback, useMemo } from "react";
import { useNavigation } from "@react-navigation/native";
import { ARTICLE_HEIGHT, FULL_WIDTH } from "../../../shared/constants/dimension";
import { ImmersiveNewsArticleRouteProp } from "../../../shared/types/RootStackParamList";
import { ROUTES } from "../../../shared/constants/routes";
import getGlobalStyles from "../../../globalStyles";
import { ModalType, useModalStore } from "../../../store/modalStore";
import { useThemeSettings } from "../../../ThemeProvider";
import { formatArticleDate } from "../../../shared/helpers/formatDate";
import { CustomIcon } from "../../../components/CustomIcon";
import CustomLinearGradient, { GRADIENT_TYPE } from "../../../components/CustomLinearGradient";
import { TrackedTouchable } from "../../../analytics/components";
import { trackInteraction } from "../../../analytics/analyticsUtil";
import { ACTION_TYPE, ANALYTICS_EVENT } from "../../../analytics/types";
import { useAudioContext } from "../../../providers/AudioProvider";
import FaviconStack from "../../../components/FaviconStack";
import { useActiveTabStore } from "../../../store/activeTabStore";
import { useCollectionStore } from "../../../store/collectionStore";
import { useActiveArticle } from "../hooks/useActiveArticle";
import { Article } from "../../../shared/types/DataTypes";
import { getImageUri } from "../../../shared/helpers/utils";
import { ImpactTag } from "../../../components/ImpactTag";

interface ArticleCardProps {
    article: Article;
    height?: number;
    fromRadio?: boolean;
    isDisable?: boolean;
}
export const ArticleCard = forwardRef<View, ArticleCardProps>(({ article, height, fromRadio, isDisable }, ref) => {
    const { theme } = useThemeSettings();
    const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);
    const { activeArticle, setActiveArticleId } = useActiveArticle();
    const activetab = useActiveTabStore((state) => state.activetab);
    const { selectedCollection } = useCollectionStore();
    const { isPlaying, isLoading, togglePlayback } = useAudioContext();
    const navigation = useNavigation<ImmersiveNewsArticleRouteProp>();
    const { setModalShown } = useModalStore();
    const playArticle = () => {
        const isCurrentArticlePlaying = isPlaying && activeArticle?.id === article?.id;

        if (isCurrentArticlePlaying) {
            togglePlayback("pause");
            return;
        }

        setActiveArticleId(article?.id);
        (navigation as any).navigate(ROUTES.HOME, { selectedTab: ROUTES.LISTEN, articleId: article?.id, collectionId: selectedCollection?.id });
        togglePlayback("play");
    }
    const onCardPress = useCallback(() => {
        if (!fromRadio) {
            trackInteraction({
                event: ANALYTICS_EVENT.INTERACTION,
                screen_name: ROUTES.HOME,
                action: ACTION_TYPE.CLICK,
                component: "home_feed_article_open",
                context: {
                    article_id: article?.id,
                    source: "home_feed",
                },
            });
        }
        // Set the active article ID when card is pressed
        (navigation as any).navigate(fromRadio ? ROUTES.RADIO_PLAYER : ROUTES.IMMERSRSIVE_NEWS_ARTICLE, { articleId: article?.id, collectionId: selectedCollection?.id });
    }, [article?.id, fromRadio, navigation, selectedCollection?.id]);
    return (
        <Card disabled={isDisable} style={styles.miniCard} onPress={onCardPress}>
            <View ref={ref} style={{ height: height || ARTICLE_HEIGHT, }}>
                <Image
                    source={getImageUri(article.image!)}
                    style={styles.fullBgImage}
                    resizeMode="cover"
                />
                {/* {article?.date && (
                    <Chip
                        mode="outlined"
                        style={[gStyles.categoryChip, {
                            position: 'absolute',
                            top: 12,
                            left: 12,
                        }]}
                        textStyle={{ ...theme.fonts.titleSmall, marginLeft: 8, marginRight: 8, color: '#fff' }}
                    >
                        {formatArticleDate(article.date, 'en-US')}
                    </Chip>)} */}
                <ImpactTag tag={article?.impactTags?.[0]} />
                 {/* <TouchableOpacity style={[styles.shareCircle, { right: 50 }]} onPress={() => { }}>
                    <CustomIcon icon="bookmark" size={16} color={theme?.colors?.white} />
                </TouchableOpacity> */}
                {fromRadio && <TrackedTouchable style={[gStyles.buttonStyle, gStyles.buttonCircle, styles.playButton, { backgroundColor: theme?.colors?.btnBgFill }]}
                    context={{ cta: activeArticle?.id === article.id && (isPlaying || isLoading) ? "AUDIO_PAUSE" : "AUDIO_PLAY", article_id: article.id }} onPress={playArticle}>
                    <CustomIcon size={24} icon={activeArticle?.id === article.id && (isPlaying || isLoading) ? 'pause' : 'play'} />
                </TrackedTouchable>}
                 <View style={styles.bottomGradientWrap}>
                    <CustomLinearGradient gradientType={GRADIENT_TYPE.ARTICLE_CARD} />
                    <View style={styles.bottomContent}>
                       {/*  <FaviconStack
                            style={styles.sourceRow}
                            favicons={article?.favicons}
                            count={0}
                            articleId={article.id}
                        /> */}
                        <Text variant={"headlineSmall"} style={{color: theme.colors.white, lineHeight:20}} numberOfLines={6}>
                            {article.title}
                        </Text>
                    </View>
                </View>
            </View>
        </Card>
    );
});

const styles = StyleSheet.create({
    playButton: {
        alignSelf: 'center',
        top: '30%',
        zIndex: 1,
        borderRadius: 35,
        width: 48,
        height: 48,
    },
    miniCard: {
        height: ARTICLE_HEIGHT,
        maxWidth: FULL_WIDTH,
        borderRadius: 16,
        overflow: "hidden",
        backgroundColor: "#000",
        elevation: 3,
        shadowColor: "#000",
        shadowOpacity: 0.08,
        shadowOffset: { width: 0, height: 6 },
    },
    fullBgImage: {
        position: "absolute",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
    },
    bottomContent: {
        paddingHorizontal: 12,
        paddingTop: 40,
        paddingBottom: 10,
    },
     bottomGradientWrap: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
    },

    miniTitle: {
        color: "#fff",
        fontSize: 14,
        fontWeight: "600",
        lineHeight: 20,
    },
    sourceRow: {
        flexDirection: "row",
        alignItems: "center",
        position: 'absolute',
        top: -25,
        right: 0,
    },

    sourceCircle: {
        width: 20,
        height: 20,
        borderRadius: 13,
        backgroundColor: "#e74c3c",
        borderWidth: 1,
        borderColor: "#fff",
    },
});
